const TOAST_STYLE_ID = 'storeeel-toast-style';
const TOAST_ENTER_DURATION_MS = 360;
const TOAST_EXIT_DURATION_MS = 460;

const TOAST_VARIANTS = {
  default: {
    className: 'Toastify__toast-theme--light Toastify__toast--default',
    background: '#dbeafe',
    color: '#1e3a8a',
  },
  success: {
    className: 'Toastify__toast-theme--light Toastify__toast--success',
    background: '#EBF8EA',
    color: '#75B572',
  },
  error: {
    className: 'Toastify__toast-theme--light Toastify__toast--error',
    background: '#ffd4d4',
    color: '#E13232',
  },
};

const escapeAttributeValue = (value) => {
  return String(value).replace(/["\\]/g, '\\$&');
};

const buildToastSelector = (attributeName, attributeValue) => {
  return `[${attributeName}="${escapeAttributeValue(attributeValue)}"]`;
};

export const ensureToastStyle = () => {
  if (document.getElementById(TOAST_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = TOAST_STYLE_ID;
  style.textContent = `
    .Toastify {
      position: fixed;
      inset: 0;
      z-index: 2147483650;
      pointer-events: none;
    }
    .Toastify__toast-container {
      position: fixed;
      right: 24px;
      bottom: 24px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      align-items: flex-end;
      pointer-events: none;
      max-width: min(320px, calc(100vw - 48px));
      box-sizing: border-box;
    }
    .Toastify__toast {
      position: relative;
      pointer-events: auto;
      box-sizing: border-box;
      min-width: 200px;
      padding: 14px;
      font-family: 'Noto Sans', Helvetica, Arial, sans-serif;
      font-size: 14px;
      font-weight: normal;
      line-height: 1.5;
      box-shadow: 0px 0px 3px #00000033;
      border-radius: 4px;
      overflow: hidden;
      display: flex;
      align-items: center;
      gap: 12px;
      justify-content: space-between;
      transform: translateX(120%);
      opacity: 0;
      transition: transform ${TOAST_ENTER_DURATION_MS}ms ease, opacity ${TOAST_ENTER_DURATION_MS}ms ease;
      will-change: transform, opacity;
    }
    .Toastify__toast.Toastify__toast--visible {
      transform: translateX(0);
      opacity: 1;
    }
    .Toastify__toast.Toastify__toast--exiting {
      transform: translateX(120%);
      opacity: 0;
    }
    .Toastify__toast-theme--light.Toastify__toast--default {
      background: #dbeafe;
      color: #1e3a8a;
    }
    .Toastify__toast-theme--light.Toastify__toast--success {
      background: #EBF8EA;
      color: #75B572;
    }
    .Toastify__toast-theme--light.Toastify__toast--error {
      background: #ffd4d4;
      color: #E13232;
    }
    .Toastify__toast-body {
      flex: 1 1 auto;
      min-width: 0;
      word-break: break-word;
    }
    .Toastify__close-button {
      flex: 0 0 auto;
      border: 0;
      background: transparent;
      color: inherit;
      font-size: 16px;
      line-height: 1;
      padding: 0;
      margin: 0;
      cursor: pointer;
      opacity: 0.8;
    }
    .Toastify__close-button:hover {
      opacity: 1;
    }
    .Toastify__progress-bar--wrp {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 3px;
      overflow: hidden;
    }
    .Toastify__progress-bar--wrp[data-hidden="true"] {
      display: none;
    }
    .Toastify__progress-bar--bg,
    .Toastify__progress-bar {
      position: absolute;
      inset: 0;
    }
    .Toastify__progress-bar--bg {
      opacity: 0.12;
      background: currentColor;
    }
    .Toastify__progress-bar {
      transform-origin: left center;
      transform: scaleX(1);
      background: currentColor;
      opacity: 0.8;
    }
  `;
  document.head.appendChild(style);
};

export const hideToast = (toastRoot, toast, {
  dataAttributeName,
  exitDurationMs = TOAST_EXIT_DURATION_MS,
} = {}) => {
  if (!toastRoot || !toast) return;
  if (toast.storeeelToastIsExiting) return;
  toast.storeeelToastIsExiting = true;
  if (dataAttributeName) {
    toast.removeAttribute(dataAttributeName);
  } else {
    delete toast.dataset.storeeelToast;
  }
  window.clearTimeout(toast.storeeelToastTimer);
  toast.classList.remove('Toastify__toast--visible');
  toast.classList.add('Toastify__toast--exiting');

  const cleanup = () => {
    toast.removeEventListener('transitionend', handleTransitionEnd);
    toastRoot.remove();
  };

  const handleTransitionEnd = (event) => {
    if (event.target !== toast) return;
    cleanup();
  };

  toast.addEventListener('transitionend', handleTransitionEnd);
  window.setTimeout(cleanup, exitDurationMs);
};

export const showToast = ({
  message,
  variant = 'default',
  duration = 6000,
  parent = document.body,
  dataAttributeName = 'data-storeeel-toast',
  dataAttributeValue = 'true',
  ariaLabel = 'Notifications Alt+T',
  closeLabel = 'close',
  positionClassName = 'Toastify__toast-container--bottom-right',
} = {}) => {
  ensureToastStyle();

  const selector = buildToastSelector(dataAttributeName, dataAttributeValue);
  const previousToast = document.querySelector(selector);
  const previousToastRoot = previousToast?.closest('.Toastify');
  if (previousToastRoot && previousToast) {
    hideToast(previousToastRoot, previousToast, {
      dataAttributeName,
    });
  }

  const toastRoot = document.createElement('section');
  toastRoot.className = 'Toastify';
  toastRoot.setAttribute('aria-live', 'polite');
  toastRoot.setAttribute('aria-atomic', 'false');
  toastRoot.setAttribute('aria-relevant', 'additions text');

  const toastContainer = document.createElement('div');
  toastContainer.className = `Toastify__toast-container ${positionClassName}`;

  const toast = document.createElement('div');
  const toastVariant = TOAST_VARIANTS[variant] || TOAST_VARIANTS.default;
  toast.className = `Toastify__toast ${toastVariant.className} Toastify__toast--close-on-click Toastify__slide-enter--bottom-right`;
  toast.setAttribute(dataAttributeName, dataAttributeValue);
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-label', ariaLabel);
  toast.style.background = toastVariant.background;
  toast.style.color = toastVariant.color;
  toast.style.fontFamily = `'Noto Sans', Helvetica, Arial, sans-serif`;
  toast.style.fontSize = '14px';
  toast.style.fontWeight = 'normal';
  toast.style.padding = '14px';
  toast.style.minWidth = '200px';
  toast.style.boxShadow = '0px 0px 3px #00000033';

  const toastBody = document.createElement('div');
  toastBody.className = 'Toastify__toast-body';
  toastBody.textContent = message;

  const closeButton = document.createElement('button');
  closeButton.className = 'Toastify__close-button Toastify__close-button--light';
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', closeLabel);
  closeButton.textContent = '×';

  const progressWrapper = document.createElement('div');
  progressWrapper.className = 'Toastify__progress-bar--wrp';
  progressWrapper.dataset.hidden = 'true';

  const progressBackground = document.createElement('div');
  progressBackground.className = 'Toastify__progress-bar--bg Toastify__progress-bar-theme--light Toastify__progress-bar--default';

  const progressBar = document.createElement('div');
  progressBar.className = 'Toastify__progress-bar Toastify__progress-bar--animated Toastify__progress-bar-theme--light Toastify__progress-bar--default';

  progressWrapper.appendChild(progressBackground);
  progressWrapper.appendChild(progressBar);
  toast.appendChild(toastBody);
  toast.appendChild(closeButton);
  toast.appendChild(progressWrapper);
  toastContainer.appendChild(toast);
  toastRoot.appendChild(toastContainer);
  parent.appendChild(toastRoot);

  window.requestAnimationFrame(() => {
    toast.classList.add('Toastify__toast--visible');
  });

  toast.storeeelToastTimer = window.setTimeout(() => {
    hideToast(toastRoot, toast, {
      dataAttributeName,
    });
  }, duration);

  closeButton.addEventListener('click', () => {
    hideToast(toastRoot, toast, {
      dataAttributeName,
    });
  });

  return {
    root: toastRoot,
    toast,
    close: () => {
      hideToast(toastRoot, toast, {
        dataAttributeName,
      });
    },
  };
};
